import { GridColDef } from "@mui/x-data-grid";
import { useEmendasParlamentaresDispatch, useEmendasParlamentaresState } from "./emendas-parlamentares-context";
import { ACTION_TYPES, initialDataRequest } from "./emendas-parlamentares-reducer";
import AdminPortalAppService from "../../../../application/platform/admin-portal/admin-portal-app-service";
import { useBaseLayoutController } from "../../../base-layout/context/base-layout-controller";
import { IEmendaParlamentarResponse } from "../../../../model/admin-portal/response/emenda-parlamentar-response";
import { ITermPaggedRequest } from "../../../../model/admin-portal/request/term-pagged-request";
import { IconButton, Box, Tooltip } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';

export const useEmendasParlamentaresController = () => {
    const state = useEmendasParlamentaresState();
    const dispatch = useEmendasParlamentaresDispatch();
    const { showMessage, showBackDrop } = useBaseLayoutController();

    const loadData = async (searchRequest: ITermPaggedRequest) => {
        try {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: true });
            const response = await AdminPortalAppService.listEmendasParlamentares(searchRequest);
            dispatch({ type: ACTION_TYPES.setDataPaginatedListResponse, payload: response });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar emendas parlamentares' });
        } finally {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: false });
        }
    };

    const onChangePagination = async (page: number, pageSize: number) => {
        const newSearch = { ...state.dataSearchRequest, page, pageSize };
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: newSearch });
        await loadData(newSearch);
    };

    const handleOnChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: { ...state.dataSearchRequest, term: e.target.value } });
    };

    const clearFields = async () => {
        const newSearch = { page: 0, pageSize: state.dataSearchRequest.pageSize, term: '' };
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: newSearch });
        await loadData(newSearch);
    };

    const loadLookups = async () => {
        try {
            const [orgaos, tipos, formas, funcoes, convenios] = await Promise.all([
                AdminPortalAppService.listOrgaos(),
                AdminPortalAppService.listTiposEmenda(),
                AdminPortalAppService.listFormasRepasseEmenda(),
                AdminPortalAppService.listFuncoesGoverno(),
                AdminPortalAppService.listConveniosLookup()
            ]);
            dispatch({ type: ACTION_TYPES.setOrgaosList, payload: orgaos });
            dispatch({ type: ACTION_TYPES.setTiposList, payload: tipos });
            dispatch({ type: ACTION_TYPES.setFormasRepasseList, payload: formas });
            dispatch({ type: ACTION_TYPES.setFuncoesGovernoList, payload: funcoes });
            dispatch({ type: ACTION_TYPES.setConveniosList, payload: convenios });
        } catch (error: any) {
            showMessage({ type: 'error', message: 'Erro ao carregar listas de apoio' });
        }
    };

    const getData = async (id: number) => {
        try {
            showBackDrop(true);
            const response = await AdminPortalAppService.getEmendaParlamentar(id);
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: response });
            dispatch({
                type: ACTION_TYPES.setDataRequest, payload: {
                    id: response.id,
                    idOrgao: response.idOrgao,
                    idTipoEmenda: response.idTipoEmenda,
                    idFormaRepasseEmenda: response.idFormaRepasseEmenda,
                    idFuncaoGoverno: response.idFuncaoGoverno,
                    idConvenio: response.idConvenio ?? null,
                    tipoOrigem: response.tipoOrigem,
                    numeroEmenda: response.numeroEmenda,
                    nomAutor: response.nomAutor,
                    textoObjetoEmenda: response.textoObjetoEmenda,
                    ano: response.ano,
                    valorPrevisto: response.valorPrevisto,
                    valorRealizado: response.valorRealizado
                }
            });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar emenda parlamentar' });
        } finally {
            showBackDrop(false);
        }
    };

    const validFields = (): boolean => {
        const r = state.dataRequest;
        if (!r.idOrgao || !r.idTipoEmenda || !r.idFormaRepasseEmenda || !r.idFuncaoGoverno) return false;
        if (!r.tipoOrigem || !r.numeroEmenda || !r.nomAutor) return false;
        if (!r.textoObjetoEmenda || r.textoObjetoEmenda.trim() === '') return false;
        if (!r.ano) return false;
        if (r.valorPrevisto === undefined || r.valorPrevisto === null || r.valorPrevisto < 0) return false;
        if (r.valorRealizado === undefined || r.valorRealizado === null || r.valorRealizado < 0) return false;
        return true;
    };

    const handleOnChangeForm = (field: string, value: any) => {
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, [field]: value } });
    };

    const openForm = (row?: IEmendaParlamentarResponse) => {
        if (row) {
            getData(row.id);
        } else {
            dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialDataRequest });
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
        }
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: true });
    };

    const closeForm = () => {
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: false });
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialDataRequest });
        dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
    };

    const save = async () => {
        if (!validFields()) {
            showMessage({ type: 'warning', message: 'Preencha todos os campos obrigatórios' });
            return;
        }

        try {
            showBackDrop(true);
            await AdminPortalAppService.addUpdateEmendaParlamentar(state.dataRequest);
            showMessage({
                type: 'success',
                message: state.dataRequest.id !== undefined && state.dataRequest.id !== null ? 'Emenda atualizada com sucesso' : 'Emenda criada com sucesso'
            });
            closeForm();
            await loadData(state.dataSearchRequest);
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao salvar emenda' });
        } finally {
            showBackDrop(false);
        }
    };

    const getColumnsGrid = (): GridColDef[] => {
        return [
            { field: 'numeroEmenda', headerName: 'Nº Emenda', flex: 1, minWidth: 130 },
            { field: 'nomAutor', headerName: 'Autor', flex: 1, minWidth: 160 },
            { field: 'tipoEmendaName', headerName: 'Modalidade', flex: 1, minWidth: 150 },
            { field: 'ano', headerName: 'Exercício', width: 100 },
            {
                field: 'valorPrevisto', headerName: 'Valor Previsto', width: 150, align: 'right', headerAlign: 'right',
                renderCell: (params) => params.value?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
            },
            {
                field: 'actions', headerName: 'Ações', width: 90, align: 'center', headerAlign: 'center', sortable: false,
                renderCell: (params) => (
                    <Box display="flex" gap={1}>
                        <Tooltip title="Editar">
                            <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openForm(params.row); }}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            }
        ];
    };

    return {
        ...state,
        loadData,
        onChangePagination,
        handleOnChangeSearch,
        clearFields,
        loadLookups,
        getData,
        validFields,
        handleOnChangeForm,
        openForm,
        closeForm,
        save,
        getColumnsGrid
    };
};
